import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@ui/Form";
import NumberInput from "@ui/NumberInput";
import { useMultiStepForm } from "@/hooks";
import { cn } from "@/utils/tailwind";

export const DynamicBillingSubform: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { t } = useTranslation();
  const { form, triggeredSteps, currentStep } = useMultiStepForm();
  const wasTriggered = triggeredSteps[currentStep.title];

  const isErrorMargin = Boolean(form.formState.errors.step3?.margin);
  const isErrorFixedFee = Boolean(form.formState.errors.step3?.fixedFee);

  const triggerValidation = () => {
    if (wasTriggered) {
      void form.trigger("step3.margin");
      void form.trigger("step3.fixedFee");
    }
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full flex-col gap-2 pl-7 sm:flex-row sm:items-start sm:pl-0">
        <FormField
          control={form.control}
          name="step3.margin"
          render={({ field }) => (
            <FormItem className="gap-1 sm:w-fit sm:px-7">
              <FormControl>
                <div className="space-y-0.5">
                  <FormDescription className={cn(disabled && "text-input")}>
                    {t("step3.q1_margin")}
                  </FormDescription>
                  <NumberInput
                    {...field}
                    dataTest="margin-input"
                    className="w-full sm:w-[172px]"
                    disabled={disabled}
                    isError={isErrorMargin}
                    onChange={value => {
                      field.onChange(value);
                      if (value) form.setValue("step3.fixedFee", "");
                      triggerValidation();
                    }}
                    unit={`${t("zl")}/${t("MWh")}`}
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />

        <p className={cn("mt-auto text-base", disabled && "text-input")}>{t("step3.or")}</p>

        <FormField
          control={form.control}
          name="step3.fixedFee"
          render={({ field }) => (
            <FormItem className="gap-1 sm:w-fit">
              <FormControl className="sm:px-7">
                <div className="space-y-0.5">
                  <FormDescription className={cn(disabled && "text-input")}>
                    {t("step3.q1_fixedFee")}
                  </FormDescription>
                  <NumberInput
                    {...field}
                    dataTest="fixed-fee-input"
                    className="w-full sm:w-[172px]"
                    disabled={disabled}
                    isError={isErrorFixedFee}
                    onChange={value => {
                      field.onChange(value);
                      if (value) form.setValue("step3.margin", "");
                      triggerValidation();
                    }}
                    unit={t("zl")}
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {(isErrorFixedFee || isErrorMargin) && (
        <FormMessage className="pl-7 sm:px-7" data-test="dynamic-error">
          {form.formState.errors.step3?.margin?.message ??
            form.formState.errors.step3?.fixedFee?.message}
        </FormMessage>
      )}
      <p className={cn("pl-7 text-sm font-light sm:px-7", disabled && "text-input")}>
        {t("step3.hintMarginAndFixedFee")}
      </p>
    </div>
  );
};
